import { useState, useCallback, useEffect } from "react";
import { useMediaQuery } from "usehooks-ts";
import { TABLET_WIDTH } from "hooks/useDeviceType";
import { metrics } from "utils/metrics";
import { IGif, INiche } from "services/types";
import { Roles } from "components/dialogs/EditNichesDialog/hooks";
import { useNavigate } from "react-router";
import { apiMain } from "services";
import { useSelector } from "react-redux";
import { IAppState } from "store/app-state";
import { MainBusEvents, MenuShareEvent, MoreMenuEvent, NicheEditEvent, mainBus } from "utils/bus";
import { Handler } from "mitt";

export const usePopupManagerState = () => {
  const navigate = useNavigate();
  const isDesktop = useMediaQuery(`(min-width: ${TABLET_WIDTH}px)`);
  const [currentPopupGif, setCurrentPopupGif] = useState<IGif>({} as IGif);
  const [currentPopupNiche, setCurrentPopupNiche] = useState<INiche>({} as INiche);
  const [isNicheOpen, setNicheOpen] = useState<boolean>(false);
  const [isBookmarkOpen, setIsBookmarkOpen] = useState<boolean>(false);
  const [isReportOpen, setReportOpen] = useState<boolean>(false);
  const [isGifPrefsOpen, setGifPrefsOpen] = useState<boolean>(false);
  const [showShareMenu, setShowShareMenu] = useState<boolean>(false);
  const [isMenuOpen, setMenuOpen] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [sourceId, setSourceId] = useState<string | null>(null);
  const [isPopupInFs, setPopupInFs] = useState<boolean>(false);
  const [hideGif, setHideGif] = useState<(id: string) => void>(() => () => {});
  const [editNicheOpen, setEditNicheOpen] = useState(false);
  const { show: isOpenAuthModal } = useSelector((state: IAppState) => state.application.authModal);
  const [editNichesDialog, setEditNichesDialog] = useState<{ canShow: boolean; role?: Roles }>({
    canShow: false,
    role: undefined,
  });

  const handleAnchorEl = useCallback((el: null | HTMLElement) => {
    setAnchorEl(el);
  }, []);

  const handleCloseShare = useCallback(() => {
    handleAnchorEl(null);
    setShowShareMenu(false);
  }, [handleAnchorEl]);

  const toggleNiche = useCallback(() => {
    setNicheOpen(prev => !prev);
    setMenuOpen(false);
  }, []);

  const toggleReport = useCallback(() => {
    setReportOpen(!isReportOpen);
    setMenuOpen(false);
  }, [isReportOpen]);

  const toggleCollection = useCallback(() => {
    setIsBookmarkOpen(prev => !prev);
    setMenuOpen(false);
  }, []);

  const toggleShareMenu = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    if (isDesktop && event) {
      setAnchorEl(event.currentTarget);
    }

    setTimeout(() => {
      setShowShareMenu(prev => {
        if (!prev) {
          metrics.sendShareClick();
        }
        return !prev;
      });
    });
    setMenuOpen(false);
  }, [isDesktop]);

  const handleOpenEditNichesDialog = useCallback((role: Roles) => {
    setEditNichesDialog({
      canShow: true,
      role,
    });
    setMenuOpen(false);
  }, []);

  const toggleGifPrefs = useCallback(() => {
    setGifPrefsOpen(prev => !prev);
    setMenuOpen(false);
  }, []);

  const handleCloseEditNichesDialog = useCallback(() => {
    setEditNichesDialog({
      canShow: true,
      role: undefined,
    });
  }, []);

  const handleCloseMenu = useCallback(() => {
    handleAnchorEl(null);
    setMenuOpen(false);
  }, [handleAnchorEl]);

  const handleOpenMenu = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    if (isDesktop) {
      handleAnchorEl(event.currentTarget);
    }

    setMenuOpen(prev => {
      if (!prev) {
        metrics.sendOptionClick();
      }

      return !prev;
    });
  }, [isDesktop, handleAnchorEl]);

  const handleHideGif = useCallback(async (gif: IGif) => {
    handleCloseMenu();

    try {
      await apiMain.hideGif({
        id: gif.id,
        home: true,
        trending: true,
      });

      hideGif(gif.id);
      console.debug(`Gif ${gif.id} hidden successfully.`);
    } catch (e) {
      console.error(`Failed to hide gif ${gif.id}.`);
    }
  }, [handleCloseMenu, hideGif]);

  const handleManageClick = useCallback((gif: IGif) => {
    handleCloseMenu();
    navigate(`/profile/manage?gifId=${gif.id}`);
  }, [navigate, handleCloseMenu]);

  const handleToggleEditNiche = useCallback(() => {
    setCurrentPopupNiche({} as INiche);
    setEditNicheOpen(prev => !prev);
  }, []);

  useEffect(() => {
    const handlers = {
      "gif_share_open": ({ gif, event }: MenuShareEvent) => {
        setCurrentPopupGif(gif);
        toggleShareMenu(event);
      },
      "gif_menu_open": ({ gif, event, hideGif, sourceId, fullscreen }: MoreMenuEvent) => {
        setCurrentPopupGif(gif);
        handleOpenMenu(event);
        setHideGif(() => hideGif);
        setPopupInFs(fullscreen);
        setSourceId(sourceId);
      },
      "niche_edit_open": ({ niche }: NicheEditEvent) => {
        setCurrentPopupNiche(niche || {} as INiche);
        setEditNicheOpen(prev => !prev);
      }
    };

    for (const [event, handler] of Object.entries(handlers)) {
      mainBus.on(event as keyof MainBusEvents, handler as Handler);
    }

    return () => {
      for (const event of Object.keys(handlers)) {
        mainBus.off(event as keyof MainBusEvents);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    isNicheOpen,
    isBookmarkOpen,
    isReportOpen,
    isGifPrefsOpen,
    showShareMenu,
    isMenuOpen,
    editNicheOpen,
    anchorEl,
    sourceId,
    isPopupInFs,
    isOpenAuthModal,
    editNichesDialog,
    currentPopupGif,
    currentPopupNiche,
    handleHideGif,
    handleManageClick,
    handleOpenEditNichesDialog,
    handleToggleEditNiche,
    toggleCollection,
    toggleNiche,
    toggleReport,
    toggleShareMenu,
    toggleGifPrefs,
    handleCloseMenu,
    handleCloseShare,
    handleCloseEditNichesDialog,
  };
};

